import React, { useEffect, useState } from "react";

function GameCatalog() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);

  const plainText = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "object") {
      return value.body || value.text || value.content || "";
    }

    return String(value);
  };

  const truncate = (value, length = 120) => {
    const text = plainText(value).trim();

    if (!text) return "";
    if (text.length <= length) return text;

    return `${text.slice(0, length)}...`;
  };

  useEffect(() => {
    fetch("/api/games", {
      headers: { Accept: "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        setGames(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading games:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedGame) return;

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setSelectedGame(null);
      }
    };

    document.body.classList.add("modal-open");
    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", handleEsc);
    };
  }, [selectedGame]);

  if (loading) {
    return (
      <div className="container mt-5">
        <p>Loading games...</p>
      </div>
    );
  }

  return (
    <>
      <section className="games-hero">
        <div className="games-hero-overlay">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <p className="games-hero-kicker">
                  Powered by Carnegie Mellon Robotics Academy
                </p>

                <h1 className="games-hero-title">Learn STEM Through Play</h1>

                <p className="games-hero-copy">
                  Explore robotics and coding through interactive adventures
                  from
                  <br />
                  Carnegie Mellon University's Robotics Academy.
                </p>

                <a
                  href="#available-games"
                  className="btn btn-danger btn-lg mt-3"
                >
                  Explore Games
                </a>
              </div>

              <div className="col-lg-6 d-none d-lg-block">
                <div className="games-hero-art">
                  <img
                    src="/hero/acceleration-city-screenshot.jpg"
                    alt="Acceleration City gameplay"
                    className="games-hero-card games-hero-card-1"
                  />

                  <img
                    src="/hero/uss-salazar-screenshot.jpg"
                    alt="Ghosts of the USS Salazar gameplay"
                    className="games-hero-card games-hero-card-2"
                  />

                  <img
                    src="/hero/belters-screenshot.jpg"
                    alt="Belters: Journey gameplay"
                    className="games-hero-card games-hero-card-3"
                  />

                  <img
                    src="/hero/zillah-beats-screenshot.jpg"
                    alt="Zillah City gameplay"
                    className="games-hero-card games-hero-card-4"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="available-games" className="container mt-5">
        <h4 className="text-muted mb-4">Available Games</h4>

        {games.length === 0 ? (
          <p className="text-center">No games are available yet.</p>
        ) : (
          <div className="row">
            {games.map((game) => {
              const descriptionText = truncate(game.description);
              const cardText = game.summary || descriptionText;

              return (
                <div className="col-md-3 mb-4" key={game.id}>
                  <div className="card h-100">
                    {game.thumbnail_url && (
                      <img
                        src={game.thumbnail_url}
                        className="card-img-top"
                        alt={game.name || "Game thumbnail"}
                      />
                    )}

                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">{game.name}</h5>

                      {cardText && <p className="card-text">{cardText}</p>}

                      <div className="mt-auto d-grid gap-2">
                        {game.url && (
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => setSelectedGame(game)}
                          >
                            Play Now
                          </button>
                        )}

                        <a
                          href={`/games/${game.slug}`}
                          className="btn btn-outline-primary"
                        >
                          Learn More
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {selectedGame && (
          <>
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              role="dialog"
              aria-modal="true"
            >
              <div className="modal-dialog modal-fullscreen m-0">
                <div className="modal-content border-0">
                  <div className="modal-header bg-dark text-white border-0">
                    <h5 className="modal-title">{selectedGame.name}</h5>

                    <div className="d-flex gap-3 align-items-center ms-auto">
                      <a
                        href={selectedGame.url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-light btn-sm px-3"
                        onClick={() => setSelectedGame(null)}
                      >
                        Open in new tab
                      </a>

                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        aria-label="Close"
                        onClick={() => setSelectedGame(null)}
                      />
                    </div>
                  </div>

                  <div className="modal-body p-0 bg-dark">
                    <iframe
                      key={selectedGame.id}
                      src={selectedGame.url}
                      title={selectedGame.name}
                      style={{
                        width: "100%",
                        height: "calc(100vh - 57px)",
                        border: "0",
                        backgroundColor: "#000",
                      }}
                      allow="fullscreen; autoplay; gamepad"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              className="modal-backdrop fade show"
              onClick={() => setSelectedGame(null)}
            />
          </>
        )}
      </div>
    </>
  );
}

export default GameCatalog;
